import React from "react";
import {Control, FieldErrors, FieldValues, UseFormRegister} from "react-hook-form";
import {Box, Button, Grid} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import {Crop} from "../../types/domain/crop";
import {CropCycle} from "../../types/domain/cropCycle";
import {SectionHeader} from "../base/SectionHeader";
import {Frame} from "../base/Frame";
import {AutocompleteInput} from "../base/AutocompleteInput";
import {LabeledInput} from "../base/LabeledInput";
import {DatePickerInput} from "../base/DatePickerInput";
import {TextInput} from "../base/TextInput";
import {FieldCreateFormInputType} from "../../utils/fieldValidationUtils";
import {convertToHasIdWithName} from "../../utils/fieldCreateUtils";

export interface Props<TFieldValues extends FieldValues = FieldCreateFormInputType> {
    mode: "edit" | "new";
    t: (key: string) => string;
    crops: Crop[];
    control: Control<TFieldValues>;
    register: UseFormRegister<TFieldValues>;
    errors: FieldErrors<TFieldValues>;
    handleAddNewCropCycle: () => void;
    initFormValues: {
        cropCycles: CropCycle[];
    };
    isAdmin: boolean;
}

export const CropWidget: React.FC<Props> = ({
    mode,
    t,
    crops,
    control,
    register,
    errors,
    handleAddNewCropCycle,
    initFormValues,
    isAdmin,
}) => {
    const cropCycle = initFormValues.cropCycles[0];

    return (
        <Grid item xs={12}>
            <Frame>
                <Grid container spacing={2}>
                    <Grid item xs={mode === "edit" ? 6 : 12}>
                        <SectionHeader label={t("dialogs.field_create.crop_widget.crop_section_title")} />
                    </Grid>
                    {mode === "edit" && (
                        <Grid item xs={6}>
                            <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                                <Button color="primary" startIcon={<AddIcon />} onClick={handleAddNewCropCycle}>
                                    {t("dialogs.field_create.crop_widget.crop_cycle_button")}
                                </Button>
                            </Box>
                        </Grid>
                    )}
                    <Grid item xs={6}>
                        <LabeledInput label={t("dialogs.field_create.crop_widget.crop")}>
                            <AutocompleteInput
                                name="cropCycles.0.cropId"
                                control={control}
                                options={crops}
                                disableClear
                                initialValue={cropCycle.cropId}
                                getOptionLabel={(option) => option.name}
                                placeholder={t("dialogs.field_create.form.automcomplete_placeholder")}
                            />
                        </LabeledInput>
                    </Grid>
                    <Grid item xs={6}>
                        <LabeledInput label={t("dialogs.field_create.crop_widget.variety")}>
                            <TextInput
                                placeholder={t("dialogs.field_create.crop_widget.variety_placeholder")}
                                name="cropCycles.0.variety"
                                type="text"
                                register={register}
                                errors={errors}
                            />
                        </LabeledInput>
                    </Grid>
                    <Grid item xs={6}>
                        <LabeledInput label={t("dialogs.field_create.crop_widget.planted_date")}>
                            <DatePickerInput name="cropCycles.0.plantedDate" control={control} errors={errors} />
                        </LabeledInput>
                    </Grid>

                    {mode === "edit" && <Grid item xs={6}></Grid>}
                    {mode === "edit" && (
                        <Grid item xs={6}>
                            <LabeledInput label={t("dialogs.field_create.crop_widget.harvested_date")}>
                                <DatePickerInput name="cropCycles.0.estHarvestDate" control={control} errors={errors} />
                            </LabeledInput>
                        </Grid>
                    )}

                    <Grid item xs={6}>
                        <LabeledInput label={t("dialogs.field_create.crop_widget.act_harvested_date")}>
                            <DatePickerInput name="cropCycles.0.actHarvestDate" control={control} errors={errors} />
                        </LabeledInput>
                    </Grid>
                    <Grid item xs={6}>
                        <LabeledInput label={t("dialogs.field_create.form.drought_sensitivity")}>
                            <AutocompleteInput
                                name="cropCycles.0.droughtSensitivity"
                                control={control}
                                options={convertToHasIdWithName([1, 2, 3, 4, 5])}
                                initialValue={cropCycle.droughtSensitivity}
                                getOptionLabel={(option) => option.name}
                            />
                        </LabeledInput>
                    </Grid>
                    <Grid item xs={6}>
                        {isAdmin && (
                            <LabeledInput label={t("dialogs.field_create.crop_widget.eff_root_depth")}>
                                <TextInput
                                    name="cropCycles.0.effRootDepth"
                                    type="number"
                                    register={register}
                                    errors={errors}
                                />
                            </LabeledInput>
                        )}
                    </Grid>
                </Grid>
            </Frame>
        </Grid>
    );
};
